import { useEffect, useMemo, useRef, useState } from "react";
import type { RoomSnapshot } from "../../shared/game";
import { StaticCard } from "../card/StaticCard";

type BattleArenaProps = {
  room: RoomSnapshot;
  playerId: string;
  phaseRemainingMs: number | null;
  onSetReady: (isReady: boolean) => void;
};

const TURN_DURATION_MS = 1100;

export function BattleArena({ room, playerId, phaseRemainingMs, onSetReady }: BattleArenaProps) {
  const battle = room.round.battle;
  const self = room.players.find((player) => player.id === playerId) ?? null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [soundEnabled, setSoundEnabled] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastTurnRef = useRef(-1);

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 100);
    return () => window.clearInterval(interval);
  }, []);

  const currentTurnIndex = useMemo(() => {
    if (!battle || room.phase !== "battle" || !room.round.battleStartedAt) {
      return battle ? battle.turns.length - 1 : -1;
    }
    const elapsed = Math.max(0, nowMs - Date.parse(room.round.battleStartedAt));
    return Math.min(battle.turns.length - 1, Math.floor(elapsed / TURN_DURATION_MS));
  }, [battle, nowMs, room.phase, room.round.battleStartedAt]);

  const currentTurn = battle?.turns[currentTurnIndex] ?? null;

  const unlockAudio = async () => {
    try {
      const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) {
        return false;
      }
      const audioContext = audioContextRef.current ?? new AudioContextClass();
      audioContextRef.current = audioContext;
      await audioContext.resume();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(520, audioContext.currentTime);
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.03, audioContext.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.08);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.08);
      setSoundEnabled(true);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const handlePointerDown = () => {
      void unlockAudio();
    };
    window.addEventListener("pointerdown", handlePointerDown, { once: true });
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (room.phase !== "battle" || !currentTurn || currentTurnIndex === lastTurnRef.current) {
      return;
    }
    lastTurnRef.current = currentTurnIndex;
    try {
      const audioContext = audioContextRef.current;
      if (!audioContext || audioContext.state !== "running") {
        return;
      }
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = currentTurn.critical ? "square" : "triangle";
      oscillator.frequency.setValueAtTime(currentTurn.critical ? 220 : 170, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(currentTurn.critical ? 440 : 240, audioContext.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.18);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.18);
    } catch {
      // Ignore autoplay / audio support failures.
    }
  }, [currentTurn, currentTurnIndex, room.phase]);

  if (!battle) {
    return null;
  }

  const attacker = currentTurn ? room.players.find((player) => player.id === currentTurn.attackerId) ?? null : null;
  const target = currentTurn ? room.players.find((player) => player.id === currentTurn.targetId) ?? null : null;
  const readyCount = room.players.filter((player) => player.connected && player.isReady).length;
  const connectedCount = room.players.filter((player) => player.connected).length;

  return (
    <main className="page-shell battle-screen-shell">
      <section className="battle-screen-panel">
        <div className="arena-header">
          <div className="arena-kicker">{room.phase === "battle" ? "Live Combat" : "Battle Results"}</div>
          <h1 className="arena-title">{room.phase === "battle" ? "Cards Clash In The Arena" : "Round Finished"}</h1>
          <div className="arena-summary">
            {room.phase === "battle"
              ? (currentTurn?.narration ?? battle.summary)
              : `${battle.summary} Ready up to return to the editor.`}
          </div>
          {!soundEnabled && (
            <div className="battle-results-banner">
              <div className="battle-results-status">
                Sound is muted until the browser allows audio.
              </div>
              <button type="button" onClick={() => { void unlockAudio(); }} className="ghost-button">
                Enable Battle Sound
              </button>
            </div>
          )}
          {room.phase === "results" && (
            <div className="battle-results-banner">
              <div className="battle-results-status">
                Ready to continue: {readyCount}/{connectedCount}
              </div>
              <button type="button" onClick={() => onSetReady(!(self?.isReady ?? false))} className={(self?.isReady ?? false) ? "active-button" : "ghost-button"}>
                {(self?.isReady ?? false) ? "Ready For Next Round" : "Ready Up To Continue"}
              </button>
            </div>
          )}
        </div>

        {attacker && target && currentTurn && (
          <div key={`${room.phase}-${currentTurnIndex}`} className="arena-duel-stage">
            <div className="arena-duel-card arena-duel-card-left arena-impact-attacker">
              <StaticCard card={attacker.submission?.card ?? attacker.card} scale={0.68} />
              <div className="arena-duel-name">{currentTurn.attackerName}</div>
            </div>

            <div className="arena-versus-column">
              <div className="arena-versus-badge">{currentTurn.critical ? "CRITICAL" : "HIT"}</div>
              <div className="arena-damage-text">-{currentTurn.damage}</div>
              <div className="arena-turn-hp">
                {currentTurn.targetName}: {currentTurn.targetHpBefore} {"->"} {currentTurn.targetHpAfter}
              </div>
              {phaseRemainingMs !== null && room.phase === "battle" && (
                <div className="arena-turn-hp">Next phase in {Math.max(0, Math.ceil(phaseRemainingMs / 1000))}s</div>
              )}
            </div>

            <div className="arena-duel-card arena-duel-card-right arena-impact-target">
              <StaticCard card={target.submission?.card ?? target.card} scale={0.68} />
              <div className="arena-duel-name">{currentTurn.targetName}</div>
            </div>
          </div>
        )}

        <div className="battle-card-grid battle-arena-grid">
          {battle.entries.map((entry) => {
            const player = room.players.find((candidate) => candidate.id === entry.playerId);
            if (!player) {
              return null;
            }
            const isAttacker = currentTurn?.attackerId === entry.playerId;
            const isTarget = currentTurn?.targetId === entry.playerId;
            return (
              <article
                key={entry.playerId}
                className={`battle-card-slot arena-combatant${isAttacker ? " arena-combatant-attacker" : ""}${isTarget ? " arena-combatant-target" : ""}`}
              >
                <div className="arena-combatant-card">
                  <StaticCard card={player.submission?.card ?? player.card} scale={0.52} />
                </div>
                <div className="battle-card-meta">
                  <strong>{entry.playerName}</strong>
                  <span>{entry.cardName} · {entry.typeLabel}</span>
                  <span>HP {entry.hpBefore} {"->"} {entry.hpAfter}</span>
                  <span>Dealt {entry.damageDealt} · Took {entry.damageTaken}</span>
                </div>
              </article>
            );
          })}
        </div>

        {room.phase === "results" && <div className="battle-results-footer" />}
      </section>
    </main>
  );
}
